import { useQuery } from '@tanstack/react-query';
import {
  getContentDetails,
  getFolder,
  getRootFolder,
  getSharedFolderContent,
} from '../components/services/UploadManagementService';
import {
  CurrentUploadContent,
  ROOT_FOLDER_ID,
} from '../components/services/UploadManagementServiceTypes';

type GetContentData = () => Promise<CurrentUploadContent>;

export const fetchFoldersWithGuid =
  ({ guid }: { guid?: string | null }): GetContentData =>
  async () => {
    if (!guid || guid === ROOT_FOLDER_ID) {
      const rootFolder = await getRootFolder();
      const contentDetails = await getContentDetails(rootFolder.contents);
      return { folderLike: rootFolder, contentDetails };
    }
    const folder = await getFolder(guid);
    const contentDetails = folder ? await getContentDetails(folder.contents) : {};
    return { folderLike: folder, contentDetails };
  };

export const useQueryFolders = (
  guid: string | null | undefined,
  options: { enabled?: boolean } = {},
) =>
  useQuery(
    ['fetchFoldersWithGuid', guid || ROOT_FOLDER_ID],
    () => {
      return fetchFoldersWithGuid({ guid })();
    },
    options,
  );

export const fetchFoldersFromSharedContent =
  ({ contentGuid, shareGuid }: { shareGuid?: string; contentGuid?: string }): GetContentData =>
  async () => {
    if (!shareGuid || !contentGuid) {
      return { folderLike: null, contentDetails: {} };
    }

    const sharedFolder = await getSharedFolderContent({ shareGuid, contentGuid });
    const contentDetails = sharedFolder ? await getContentDetails(sharedFolder.contents) : {};
    return { folderLike: sharedFolder, contentDetails };
  };

export const useQuerySharedFolders = ({
  contentGuid,
  shareGuid,
}: {
  shareGuid?: string;
  contentGuid?: string;
}) =>
  useQuery(['fetchFoldersFromSharedContent', contentGuid, shareGuid], () => {
    return fetchFoldersFromSharedContent({ contentGuid, shareGuid })();
  });
